import { useState, useEffect } from 'react';
import { PlusIcon, PackageIcon, SearchIcon, PencilIcon, TrashIcon } from 'lucide-react';
import SourceSelectionModal from '../components/SourceSelectionModal';
import { ProductUploadModal } from '../components/ProductUploadModal';
import EditProductModal from '../components/EditProductModal';
import { ConfirmationModal, PageLoader } from '../components/common';
import { tenantApi, Product } from '../lib/tenant-api';
import { useEventNotifier } from '../contexts/EventNotifierContext';

export default function ProductsManagement() {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState<Product | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { subscribe } = useEventNotifier();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const data = await tenantApi.getProducts();
      setProducts(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = subscribe('refresh', (data) => {
      console.log('EventNotifier received refresh:', data);
      if (data.message == "products") {
        loadProducts();
      }
    });

    return () => unsubscribe();
  }, [subscribe]);

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      setIsDeleting(true);
      await tenantApi.deleteProduct(productToDelete.id);

      // Automatically delete associated embeddings
      try {
        await tenantApi.deleteEmbeddings('product', productToDelete.id);
      } catch (embeddingError: any) {
        // Log embedding deletion error but don't fail the product deletion
        console.warn(`Failed to delete embeddings for product ${productToDelete.id}:`, embeddingError);
      }

      await loadProducts();
      setDeleteModalOpen(false);
      setProductToDelete(null);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete product');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditProduct = (product: Product) => {
    setProductToEdit(product);
    setEditModalOpen(true);
  };

  const handleSaveProduct = async (productData: any) => {
    if (!productToEdit) return;

    await tenantApi.updateProduct(productToEdit.id, productData);
    await loadProducts();
    setEditModalOpen(false);
    setProductToEdit(null);
    setError(null);
  };

  const handleAddProduct = () => {
    setIsSourceModalOpen(true);
  };

  const handleManualUpload = () => {
    setIsSourceModalOpen(false);
    setIsUploadModalOpen(true);
  };

  // Filter products based on search term
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const EmptyState = () => (
    <div className="text-center py-12">
      <PackageIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
      <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
        Get started by adding your first product. You can upload product information, descriptions, and details to help customers learn about your offerings.
      </p>
      <div className="mt-6">
        <button
          type="button"
          onClick={handleAddProduct}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium  text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Add Product
        </button>
      </div>
    </div>
  );

  const SearchEmptyState = () => (
    <div className="text-center py-12">
      <SearchIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-4 text-lg font-medium text-gray-900">No products match your search</h3>
      <p className="mt-2 text-sm text-gray-500">
        Try adjusting your search terms or browse all products.
      </p>
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setSearchTerm('')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium  text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Clear search
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your product catalog and information
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddProduct}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium  text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Add Product
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300  leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Search products..."
            />
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 ">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="bg-white shadow ">
        {isLoading ? (
          <PageLoader />
        ) : products.length === 0 ? (
          <EmptyState />
        ) : filteredProducts.length === 0 ? (
          <SearchEmptyState />
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredProducts.map((product) => (
              <li key={product.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {product.name}
                      </h3>
                      {product.category && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {product.category}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                      {product.description}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {product.source_file_name && (
                        <span>Source: {product.source_file_name}</span>
                      )}
                      {product.price && (
                        <span>Price: ${product.price.toFixed(2)}</span>
                      )}
                      <span>Created: {new Date(product.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      type="button"
                      onClick={() => handleEditProduct(product)}
                      className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 "
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setProductToDelete(product);
                        setDeleteModalOpen(true);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 "
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Stats Footer */}
      {products.length > 0 && (
        <div className="mt-6 flex justify-between items-center text-sm text-gray-500">
          <span>Showing {products.length} product{products.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Source Selection Modal */}
      <SourceSelectionModal
        isOpen={isSourceModalOpen}
        onClose={() => setIsSourceModalOpen(false)}
        contentType="products"
        onManualUpload={handleManualUpload}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteProduct}
        title="Delete Product"
        message={`Are you sure you want to delete the product "${productToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Edit Product Modal */}
      {productToEdit && (
        <EditProductModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleSaveProduct}
          product={productToEdit}
        />
      )}

      {/* Product Upload Modal */}
      <ProductUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={() => {
          loadProducts();
          setIsUploadModalOpen(false);
        }}
      />
    </div>
  );
}
